import { Component, Input, inject, computed, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponentV2 } from '../icon-component/icon.component';
import { ThemeService } from '../../../core/services/theme.service';
import { LoaderService } from '../../../core/services/loader.service';

type LoaderSize = 'sm' | 'md' | 'lg' | 'xl';

@Component({
  selector: 'app-global-loader',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (loaderService.state().show) {
    <div class="global-loader-container" [style.background-color]="loaderBackground()">
      <div class="global-loader-wrapper">
        <div class="circuit-loader">
          <svg viewBox="0 0 800 500" xmlns="http://www.w3.org/2000/svg" class="circuit-svg">
            <defs>
              <linearGradient id="chipGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" [attr.stop-color]="chipGradientStart()"></stop>
                <stop offset="100%" [attr.stop-color]="chipGradientEnd()"></stop>
              </linearGradient>

              <linearGradient id="textGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" [attr.stop-color]="textGradientStart()"></stop>
                <stop offset="100%" [attr.stop-color]="textGradientEnd()"></stop>
              </linearGradient>

              <linearGradient id="pinGradient" x1="1" y1="0" x2="0" y2="0">
                <stop offset="0%" [attr.stop-color]="pinGradientStart()"></stop>
                <stop offset="50%" [attr.stop-color]="pinGradientMid()"></stop>
                <stop offset="100%" [attr.stop-color]="pinGradientEnd()"></stop>
              </linearGradient>

              <filter id="glow">
                <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>

            <g id="traces">
              <path d="M100 100 H200 V210 H350" class="trace-bg"></path>
              <path d="M100 100 H200 V210 H350" class="trace-flow trace-flow-1" [attr.stroke]="traceFlowColor1()"></path>

              <path d="M80 180 H180 V230 H350" class="trace-bg"></path>
              <path d="M80 180 H180 V230 H350" class="trace-flow trace-flow-2" [attr.stroke]="traceFlowColor2()"></path>

              <path d="M60 260 H150 V250 H350" class="trace-bg"></path>
              <path d="M60 260 H150 V250 H350" class="trace-flow trace-flow-3" [attr.stroke]="traceFlowColor1()"></path>

              <path d="M100 350 H200 V270 H350" class="trace-bg"></path>
              <path d="M100 350 H200 V270 H350" class="trace-flow trace-flow-4" [attr.stroke]="traceFlowColor2()"></path>

              <path d="M700 90 H560 V210 H450" class="trace-bg"></path>
              <path d="M700 90 H560 V210 H450" class="trace-flow trace-flow-5" [attr.stroke]="traceFlowColor2()"></path>

              <path d="M740 160 H580 V230 H450" class="trace-bg"></path>
              <path d="M740 160 H580 V230 H450" class="trace-flow trace-flow-6" [attr.stroke]="traceFlowColor1()"></path>

              <path d="M720 250 H590 V250 H450" class="trace-bg"></path>
              <path d="M720 250 H590 V250 H450" class="trace-flow trace-flow-7" [attr.stroke]="traceFlowColor2()"></path>

              <path d="M680 340 H570 V270 H450" class="trace-bg"></path>
              <path d="M680 340 H570 V270 H450" class="trace-flow trace-flow-8" [attr.stroke]="traceFlowColor1()"></path>
            </g>

            <rect x="350" y="210" width="100" height="70" rx="15" ry="15" fill="url(#chipGradient)" 
                  [attr.stroke]="chipBorderColor()" stroke-width="2.5" filter="url(#glow)" class="chip-body"></rect>

            <g class="chip-pins-left">
              <rect x="342" y="220" width="8" height="10" fill="url(#pinGradient)" rx="2"></rect>
              <rect x="342" y="235" width="8" height="10" fill="url(#pinGradient)" rx="2"></rect>
              <rect x="342" y="250" width="8" height="10" fill="url(#pinGradient)" rx="2"></rect>
              <rect x="342" y="265" width="8" height="10" fill="url(#pinGradient)" rx="2"></rect>
            </g>

            <g class="chip-pins-right">
              <rect x="450" y="220" width="8" height="10" fill="url(#pinGradient)" rx="2"></rect>
              <rect x="450" y="235" width="8" height="10" fill="url(#pinGradient)" rx="2"></rect>
              <rect x="450" y="250" width="8" height="10" fill="url(#pinGradient)" rx="2"></rect>
              <rect x="450" y="265" width="8" height="10" fill="url(#pinGradient)" rx="2"></rect>
            </g>

            <text x="400" y="245" font-family="system-ui, -apple-system, sans-serif" font-size="18" 
                  font-weight="600" fill="url(#textGradient)" text-anchor="middle" 
                  alignment-baseline="middle" class="chip-text">
              Loading
            </text>

            <g class="connection-points">
              <circle cx="100" cy="100" r="5" [attr.fill]="connectionPointColor()"></circle>
              <circle cx="80" cy="180" r="5" [attr.fill]="connectionPointColor()"></circle>
              <circle cx="60" cy="260" r="5" [attr.fill]="connectionPointColor()"></circle>
              <circle cx="100" cy="350" r="5" [attr.fill]="connectionPointColor()"></circle>
              <circle cx="700" cy="90" r="5" [attr.fill]="connectionPointColor()"></circle>
              <circle cx="740" cy="160" r="5" [attr.fill]="connectionPointColor()"></circle>
              <circle cx="720" cy="250" r="5" [attr.fill]="connectionPointColor()"></circle>
              <circle cx="680" cy="340" r="5" [attr.fill]="connectionPointColor()"></circle>
            </g>
          </svg>
        </div>
      </div>
    </div>
    }
  `,
  styles: [`
    :host {
      display: contents;
    }

    .global-loader-container {
      position: fixed;
      inset: 0;
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
      backdrop-filter: blur(14px);
      -webkit-backdrop-filter: blur(14px);
    }

    .global-loader-wrapper {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      padding: 2rem;
    }

    .circuit-loader {
      width: 100%;
      max-width: 500px;
      height: auto;
      aspect-ratio: 8 / 5;
    }

    .circuit-svg {
      width: 100%;
      height: 100%;
      filter: drop-shadow(0 4px 16px rgba(139, 92, 246, 0.15));
    }

    .trace-bg {
      stroke: var(--color-border, #e2e8f0);
      stroke-width: 1.8;
      fill: none;
      opacity: 0.25;
    }

    .trace-flow {
      stroke-width: 2.2;
      fill: none;
      stroke-dasharray: 40 400;
      stroke-dashoffset: 438;
      filter: drop-shadow(0 0 6px currentColor);
      animation: flow 1.2s cubic-bezier(0.5, 0, 0.9, 1) infinite;
    }

    @keyframes flow {
      to {
        stroke-dashoffset: 0;
      }
    }

    .chip-body {
      transition: all 0.3s ease;
    }

    .chip-text {
      letter-spacing: 0.5px;
      user-select: none;
      font-weight: 600;
    }

    .chip-pins-left,
    .chip-pins-right {
      filter: drop-shadow(0 0 3px rgba(139, 92, 246, 0.3));
    }

    .connection-points circle {
      transition: all 0.3s ease;
    }

    .trace-flow-2 { animation-delay: 0.15s; }
    .trace-flow-3 { animation-delay: 0.3s; }
    .trace-flow-4 { animation-delay: 0.45s; }
    .trace-flow-5 { animation-delay: 0.6s; }
    .trace-flow-6 { animation-delay: 0.75s; }
    .trace-flow-7 { animation-delay: 0.9s; }
    .trace-flow-8 { animation-delay: 1.05s; }

    @media (max-width: 768px) {
      .circuit-loader {
        max-width: 90vw;
      }
    }

    @media (max-width: 480px) {
      .circuit-loader {
        max-width: 95vw;
      }
    }
  `],
})
export class GlobalLoaderComponent {
  readonly loaderService = inject(LoaderService);
  private readonly themeService = inject(ThemeService);

  readonly loaderBackground = computed(() => {
    return this.themeService.theme() === 'dark'
      ? 'rgba(11, 11, 13, 0.6)'
      : 'rgba(255, 255, 255, 0.35)';
  });

  private readonly lightColors = {
    chipGradientStart: '#ffffff',
    chipGradientEnd: '#f5f3ff',
    chipBorderColor: '#c4b5fd',
    textGradientStart: '#6d28d9',
    textGradientEnd: '#7c3aed',
    pinGradientStart: '#ddd6fe',
    pinGradientMid: '#c4b5fd',
    pinGradientEnd: '#a78bfa',
    connectionPointColor: '#8b5cf6',
    traceFlowColor1: '#8b5cf6',
    traceFlowColor2: '#a855f7',
  };

  private readonly darkColors = {
    chipGradientStart: '#1e1b4b',
    chipGradientEnd: '#0f0a2e',
    chipBorderColor: '#4c1d95',
    textGradientStart: '#e9d5ff',
    textGradientEnd: '#c084fc',
    pinGradientStart: '#6d28d9',
    pinGradientMid: '#5b21b6',
    pinGradientEnd: '#4c1d95',
    connectionPointColor: '#c084fc',
    traceFlowColor1: '#a78bfa',
    traceFlowColor2: '#c084fc',
  };

  // Computed color values that react to theme changes
  readonly chipGradientStart = computed(() => 
    this.themeService.theme() === 'dark' ? this.darkColors.chipGradientStart : this.lightColors.chipGradientStart
  );

  readonly chipGradientEnd = computed(() => 
    this.themeService.theme() === 'dark' ? this.darkColors.chipGradientEnd : this.lightColors.chipGradientEnd
  );

  readonly chipBorderColor = computed(() => 
    this.themeService.theme() === 'dark' ? this.darkColors.chipBorderColor : this.lightColors.chipBorderColor
  );

  readonly textGradientStart = computed(() => 
    this.themeService.theme() === 'dark' ? this.darkColors.textGradientStart : this.lightColors.textGradientStart
  );

  readonly textGradientEnd = computed(() => 
    this.themeService.theme() === 'dark' ? this.darkColors.textGradientEnd : this.lightColors.textGradientEnd
  );

  readonly pinGradientStart = computed(() => 
    this.themeService.theme() === 'dark' ? this.darkColors.pinGradientStart : this.lightColors.pinGradientStart
  );

  readonly pinGradientMid = computed(() => 
    this.themeService.theme() === 'dark' ? this.darkColors.pinGradientMid : this.lightColors.pinGradientMid
  );

  readonly pinGradientEnd = computed(() => 
    this.themeService.theme() === 'dark' ? this.darkColors.pinGradientEnd : this.lightColors.pinGradientEnd
  );

  readonly connectionPointColor = computed(() => 
    this.themeService.theme() === 'dark' ? this.darkColors.connectionPointColor : this.lightColors.connectionPointColor
  );

  readonly traceFlowColor1 = computed(() => 
    this.themeService.theme() === 'dark' ? this.darkColors.traceFlowColor1 : this.lightColors.traceFlowColor1
  );

  readonly traceFlowColor2 = computed(() => 
    this.themeService.theme() === 'dark' ? this.darkColors.traceFlowColor2 : this.lightColors.traceFlowColor2
  );
}

@Component({
  selector: 'app-inline-loader',
  standalone: true,
  imports: [CommonModule, IconComponentV2],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="flex items-center justify-center gap-3 p-8">
      <app-icon-v2 name="LoaderCircle" [size]="iconSize" customClass="animate-spin text-primary" />
      @if (text) {
        <p class="text-sm text-muted-foreground">{{ text }}</p>
      }
    </div>
  `,
})
export class InlineLoaderComponent {
  @Input() size: LoaderSize = 'md';
  @Input() text?: string;

  get iconSize(): string {
    const sizes = {
      sm: '16',
      md: '24',
      lg: '32',
      xl: '48',
    };
    return sizes[this.size];
  }
}

@Component({
  selector: 'app-card-loader',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="space-y-3 p-6 animate-pulse">
      <div class="h-4 bg-muted rounded w-3/4"></div>
      <div class="h-4 bg-muted rounded w-1/2"></div>
      <div class="h-4 bg-muted rounded w-5/6"></div>
    </div>
  `,
})
export class CardLoaderComponent {}

@Component({
  selector: 'app-skeleton-loader',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="space-y-4 animate-pulse">
      <div class="flex items-center gap-4">
        <div class="size-12 rounded-full bg-muted"></div>
        <div class="space-y-2 flex-1">
          <div class="h-4 bg-muted rounded w-1/4"></div>
          <div class="h-3 bg-muted rounded w-1/3"></div>
        </div>
      </div>
      <div class="space-y-2">
        <div class="h-4 bg-muted rounded"></div>
        <div class="h-4 bg-muted rounded w-5/6"></div>
        <div class="h-4 bg-muted rounded w-4/6"></div>
      </div>
    </div>
  `,
})
export class SkeletonLoaderComponent {}

@Component({
  selector: 'app-dots-loader',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="flex gap-2">
      <div class="size-3 rounded-full bg-primary animate-bounce" style="animation-delay: -0.3s;"></div>
      <div class="size-3 rounded-full bg-primary animate-bounce" style="animation-delay: -0.15s;"></div>
      <div class="size-3 rounded-full bg-primary animate-bounce"></div>
    </div>
  `,
})
export class DotsLoaderComponent {}

@Component({
  selector: 'app-progress-loader',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="space-y-2">
      <div class="flex justify-between text-sm">
        <span class="text-muted-foreground">{{ loadingText }}</span>
        <span class="text-muted-foreground">{{ progress }}%</span>
      </div>
      <div class="h-2 bg-muted rounded-full overflow-hidden">
        <div
          class="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 transition-all duration-300 ease-out"
          [style.width.%]="progress"></div>
      </div>
    </div>
  `,
})
export class ProgressLoaderComponent {
  @Input() progress: number = 0;
  @Input() loadingText: string = 'Loading...';
}

